import { useEffect, useMemo, useReducer } from 'react';
import { fetch, fetchRecommendations } from './cardListActions';
import { initialState, playbooksCardListReducer } from './cardListReducer';
import useDebounce from '../../util/useDebounce';
import useThunkDispatch from '../../util/useThunkDispatch';
import { switchToCardListView } from './cardListActionTypes';
const isEnabledForPlaybooks = service => service.scopes.includes('playbooks-read');
export default function useCardListViewModel({
  service,
  objectType,
  objectId
}) {
  const [state, dispatch] = useReducer(playbooksCardListReducer, initialState);
  const thunkDispatch = useThunkDispatch(state, dispatch, service);
  const handleSearch = useDebounce(350, searchText => {
    thunkDispatch(fetch({
      offset: 0,
      searchText
    }));
    service.logInteraction('playbookInteraction', {
      action: 'search-card',
      playbookId: 0,
      playbookType: ''
    });
  });
  const actions = useMemo(() => {
    return {
      fetch() {
        thunkDispatch(fetch({
          limit: service.playbookListLimit
        }));
        thunkDispatch(fetchRecommendations(objectType, objectId));
      },
      handlePageChange({
        target: {
          value
        }
      }) {
        const page = value;
        const newOffset = (page - 1) * service.playbookListLimit;
        thunkDispatch(fetch({
          offset: newOffset,
          limit: service.playbookListLimit
        }));
      },
      onSearchInputChange(evt) {
        handleSearch(evt.currentTarget.value);
      },
      openPlaybookModal(playbookWithRecommendation) {
        const {
          playbook,
          recommendationType
        } = playbookWithRecommendation;
        service.openPlaybook(playbook.contentId);
        service.logInteraction('playbookInteraction', {
          playbookId: playbook.contentId,
          playbookType: playbook.metadata.playbookType,
          action: `opened ${recommendationType !== 'NONE' ? 'recommended playbook' : 'playbook'}`,
          what_value_str: recommendationType
        });
      },
      showAllPlaybooks() {
        dispatch(switchToCardListView('ALL_PLAYBOOKS'));
      }
    };
  }, [thunkDispatch, objectType, objectId, handleSearch, service]);
  useEffect(() => {
    if (isEnabledForPlaybooks(service)) {
      actions.fetch();
    }
  }, [actions, service]);
  const recommendedIds = useMemo(() => new Set(state.playbooks.hoistedRecommendations.map(rec => rec.playbook.contentId)), [state.playbooks.hoistedRecommendations]);
  const selectedState = useMemo(() => {
    const totalPlaybooksCount = state.playbooks.total;
    const isError = false;
    const hasQuery = state.searchText.length > 0;
    const hasRecommendations = state.playbooks.hoistedRecommendations.length > 0;
    const isOnFirstPage = state.requestedPlaybooks.offset === 0;
    const showRecommendations = !hasQuery && hasRecommendations && isOnFirstPage;
    const playbooksResults = showRecommendations ? state.playbooks.results.filter(rec => !recommendedIds.has(rec.playbook.contentId)) : state.playbooks.results;
    return {
      hasQueryOrResults: hasQuery || totalPlaybooksCount !== 0,
      showRecommendations,
      showPaginator: totalPlaybooksCount > service.playbookListLimit && !isError,
      loading: state.loading,
      recommendationResult: {
        total: state.playbooks.hoistedRecommendations.length,
        offset: 0,
        results: state.playbooks.hoistedRecommendations
      },
      playbooksResult: {
        offset: state.playbooks.offset,
        total: state.playbooks.total,
        results: playbooksResults
      },
      searchText: state.searchText,
      searchQuery: {
        offset: state.requestedPlaybooks.offset,
        limit: service.playbookListLimit
      },
      totalPlaybooksCount,
      failedToLoad: state.searchFailed && !hasRecommendations,
      isEnabledForPlaybooks: isEnabledForPlaybooks(service),
      showAllPlaybooks: state.cardListView === 'ALL_PLAYBOOKS'
    };
  }, [state, recommendedIds, service]);
  return [selectedState, actions];
}